<template>
  <header class="app-header">
    <div class="header-left">
      <div class="logo">
        <span class="logo-icon">🖨️</span>
        <span class="logo-text">打印模板编辑器</span>
      </div>
      
      <!-- 文件操作 -->
      <div class="file-actions">
        <button @click="importTemplate" class="header-btn" title="导入模板">
          <span class="btn-icon">📁</span>
          导入
        </button>
        <button @click="exportTemplate" class="header-btn" title="导出模板">
          <span class="btn-icon">💾</span>
          保存
        </button>
      </div>
    </div>
    
    <div class="header-center">
      <!-- 工具栏 -->
      <div class="toolbar">
        <!-- 撤销重做 -->
        <div class="tool-group">
          <button 
            @click="undo" 
            :disabled="!canUndo"
            class="tool-btn"
            title="撤销"
          >
            <span class="btn-icon">↶</span>
          </button>
          <button 
            @click="redo" 
            :disabled="!canRedo"
            class="tool-btn"
            title="重做"
          >
            <span class="btn-icon">↷</span>
          </button>
        </div>
        
        <!-- 缩放控制 -->
        <div class="tool-group">
          <label>缩放:</label>
          <select 
            :value="zoomLevel" 
            @change="setZoomLevel($event.target.value)"
            class="zoom-select"
          >
            <option value="50">50%</option>
            <option value="75">75%</option>
            <option value="100">100%</option>
            <option value="125">125%</option>
            <option value="150">150%</option>
            <option value="200">200%</option>
          </select>
        </div>
        
        <!-- 字体控制 -->
        <div class="tool-group">
          <select 
            :value="fontFamily" 
            @change="setFontFamily($event.target.value)"
            class="font-select"
          >
            <option value="微软雅黑">微软雅黑</option>
            <option value="宋体">宋体</option>
            <option value="黑体">黑体</option>
            <option value="楷体">楷体</option>
          </select>
          
          <select 
            :value="fontSize" 
            @change="setFontSize($event.target.value)"
            class="font-size-select"
          >
            <option value="12">12</option>
            <option value="14">14</option>
            <option value="16">16</option>
            <option value="18">18</option>
            <option value="20">20</option>
            <option value="24">24</option>
          </select>
        </div>
      </div>
    </div>
    
    <div class="header-right">
      <!-- 操作按钮 -->
      <div class="action-buttons">
        <button @click="previewPrint" class="action-btn preview-btn">
          <span class="btn-icon">👁️</span>
          打印预览
        </button>
        <button @click="printTemplate" class="action-btn print-btn">
          <span class="btn-icon">🖨️</span>
          直接打印
        </button>
        <button @click="closeEditor" class="action-btn close-btn">
          <span class="btn-icon">❌</span>
          关闭
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import { computed } from 'vue'
import { useStore } from 'vuex'
import { TemplateService } from '@/services/templateService'

export default {
  name: 'AppHeader',
  emits: ['preview-print', 'print-template', 'close-editor'],
  
  setup(props, { emit }) {
    const store = useStore()
    
    // 计算属性
    const zoomLevel = computed(() => store.state.template.zoomLevel)
    const fontFamily = computed(() => store.state.template.fontFamily || '微软雅黑')
    const fontSize = computed(() => store.state.template.fontSize || 14)
    const canUndo = computed(() => store.getters['canvas/canUndo'])
    const canRedo = computed(() => store.getters['canvas/canRedo'])
    const templateConfig = computed(() => store.state.template.config)
    const elements = computed(() => store.state.canvas.elements)
    
    // 方法
    const setZoomLevel = (level) => {
      store.dispatch('template/setZoomLevel', parseInt(level))
    }
    
    const setFontFamily = (family) => {
      store.commit('template/SET_FONT_FAMILY', family)
    }
    
    const setFontSize = (size) => {
      store.commit('template/SET_FONT_SIZE', parseInt(size))
    }
    
    const undo = () => {
      store.dispatch('canvas/undo')
    }
    
    const redo = () => {
      store.dispatch('canvas/redo')
    }
    
    const importTemplate = () => {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      input.onchange = async (e) => {
        const file = e.target.files[0]
        if (!file) return
        
        try {
          const templateData = await TemplateService.importTemplate(file)
          
          // 验证模板
          const validation = TemplateService.validateTemplate(templateData.config)
          if (!validation.isValid) {
            alert(`模板验证失败: ${validation.errors.join(', ')}`)
            return
          }
          
          // 导入到store
          Object.entries(templateData.config).forEach(([key, value]) => {
            store.dispatch('template/updateConfig', { key, value })
          })
          
          store.commit('canvas/SET_ELEMENTS', templateData.elements || [])
          
          alert('模板导入成功!')
        } catch (error) {
          alert(`导入失败: ${error.message}`)
        }
      }
      input.click()
    }
    
    const exportTemplate = () => {
      try {
        TemplateService.exportTemplate(templateConfig.value, elements.value)
        // 显示成功提示
        const toast = document.createElement('div')
        toast.textContent = '模板保存成功!'
        toast.style.cssText = `
          position: fixed;
          top: 20px;
          right: 20px;
          background: #28a745;
          color: white;
          padding: 12px 20px;
          border-radius: 4px;
          z-index: 10000;
          animation: slideIn 0.3s ease;
        `
        document.body.appendChild(toast)
        setTimeout(() => {
          document.body.removeChild(toast)
        }, 3000)
      } catch (error) {
        alert(`保存失败: ${error.message}`)
      }
    }
    
    const previewPrint = async () => {
      emit('preview-print')
    }
    
    const printTemplate = async () => {
      emit('print-template')
    }
    
    const closeEditor = () => {
      emit('close-editor')
    }
    
    return {
      zoomLevel,
      fontFamily,
      fontSize,
      canUndo,
      canRedo,
      setZoomLevel,
      setFontFamily,
      setFontSize,
      undo,
      redo,
      importTemplate,
      exportTemplate,
      previewPrint,
      printTemplate,
      closeEditor
    }
  }
}
</script>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  border-bottom: 1px solid rgba(255,255,255,0.1);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.logo {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  font-size: 18px;
}

.logo-icon {
  font-size: 24px;
}

.file-actions {
  display: flex;
  gap: 8px;
}

.header-center {
  flex: 1;
  display: flex;
  justify-content: center;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 8px 16px;
  background: rgba(255,255,255,0.1);
  border-radius: 8px;
  backdrop-filter: blur(10px);
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tool-group label {
  font-size: 14px;
  opacity: 0.9;
}

.tool-btn {
  padding: 6px 10px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.1);
  color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;
}

.tool-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.5);
}

.tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.zoom-select,
.font-select,
.font-size-select {
  padding: 4px 8px;
  border: 1px solid rgba(255,255,255,0.3);
  background: rgba(255,255,255,0.1);
  color: white;
  border-radius: 4px;
  font-size: 14px;
}

.zoom-select option,
.font-select option,
.font-size-select option {
  background: #333;
  color: white;
}

.header-right {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.header-btn,
.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.header-btn {
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
}

.header-btn:hover {
  background: rgba(255,255,255,0.3);
  border-color: rgba(255,255,255,0.5);
}

.preview-btn {
  background: #17a2b8;
  color: white;
}

.preview-btn:hover {
  background: #138496;
}

.print-btn {
  background: #28a745;
  color: white;
}

.print-btn:hover {
  background: #218838;
}

.close-btn {
  background: #dc3545;
  color: white;
}

.close-btn:hover {
  background: #c82333;
}

.btn-icon {
  font-size: 16px;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .toolbar {
    gap: 12px;
  }
  
  .tool-group {
    gap: 4px;
  }
}

@media (max-width: 768px) {
  .app-header {
    flex-direction: column;
    gap: 12px;
    padding: 12px;
  }
  
  .header-center {
    width: 100%;
  }
  
  .toolbar {
    width: 100%;
    justify-content: space-between;
  }
  
  .action-buttons {
    gap: 8px;
  }
  
  .action-btn {
    padding: 6px 12px;
    font-size: 12px;
  }
}

/* 动画效果 */
@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
</style>
